import { useMemo } from 'react';
import { enrichQuotesWithROI } from '@shared';

import { adjustQuotesForPremium } from '@client/util/quoteAdjustment';

import {
  applyPaymentPreferenceToPremiumBounds,
  computePremiumBoundsWithFallback,
  type PremiumBounds,
} from '@/features/guided-financial-planning/utils/premium-bounds-from-quotes';
import {
  type GuidedPlanningQuote,
  getQuoteId,
  isTraditionalLTCQuote,
} from './use-quotes-display';

interface UsePremiumAdjustmentOptions {
  quotes: GuidedPlanningQuote[] | undefined;
  targetPremium: number | undefined;
  client: Client | undefined;
  paymentPreference: PaymentPreference | undefined;
}

interface UsePremiumAdjustmentResult {
  quotes: GuidedPlanningQuote[];
  premiumBounds: PremiumBounds;
}

export function usePremiumAdjustment({
  quotes,
  targetPremium,
  client,
  paymentPreference,
}: UsePremiumAdjustmentOptions): UsePremiumAdjustmentResult {
  const premiumBounds = useMemo(() => {
    const bounds = computePremiumBoundsWithFallback(quotes);
    return applyPaymentPreferenceToPremiumBounds(bounds, paymentPreference ?? 'no-preference');
  }, [quotes, paymentPreference]);

  const adjustedQuotes = useMemo(() => {
    if (!quotes || quotes.length === 0) {
      return [];
    }

    if (targetPremium === undefined) {
      return quotes;
    }

    const clampedTargetPremium = Math.max(premiumBounds.min, Math.min(premiumBounds.max, targetPremium));

    const adjustableQuotes = quotes.filter(quote => !isTraditionalLTCQuote(quote));
    const adjustedPolicyQuotes = adjustQuotesForPremium(
      adjustableQuotes as unknown as PolicyQuote[],
      clampedTargetPremium,
    ) as unknown as GuidedPlanningQuote[];
    const adjustedPolicyById = new Map(adjustedPolicyQuotes.map(quote => [getQuoteId(quote), quote]));

    const premiumAdjustedQuotes = quotes.map(quote => {
      if (isTraditionalLTCQuote(quote)) {
        return quote;
      }

      return adjustedPolicyById.get(getQuoteId(quote)) ?? quote;
    });

    if (!client) {
      return premiumAdjustedQuotes;
    }

    return enrichQuotesWithROI(
      premiumAdjustedQuotes as unknown as PolicyQuote[],
      client,
      'total',
      { handleTraditionalLTC: true },
    ) as GuidedPlanningQuote[];
  }, [quotes, targetPremium, premiumBounds.min, premiumBounds.max, client]);

  return {
    quotes: adjustedQuotes,
    premiumBounds,
  };
}
